import { Heading, Body } from "@/components/Typography";
import Balancer from "react-wrap-balancer";
import GetStartedButton from "@/components/Buttons/GetStartedButton";
import Image from "next/image";
import { css } from "@emotion/react";
import { WhoWeServePageFields } from "./WhoWeServeTemplate";
import ReviewBadgeHero2 from "@/components/Homepage/ReviewBadgeHero2";
import HomepageHeroTestimonials from "@/components/Homepage/HomepageHeroTestimonials";
import { useWindowSize } from "@/hooks/useWindowSize";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { mq } from "@/constants/breakpoints";
import Container from "../Container/Container";

interface SmallScreenHeroProps {
  fields: WhoWeServePageFields;
  contentfulLoader: any;
  heroBackgroundIsDark?: boolean;
}

export const SmallScreenHero = ({
  fields,
  contentfulLoader,
  heroBackgroundIsDark = false,
}: SmallScreenHeroProps) => {
  const { isXs, isSm, isMd } = useWindowSize();
  const router = useRouter();
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  return (
    <div
      css={css`
        text-align: center;
        margin-top: 16px;
        color: ${heroBackgroundIsDark ? "#FFFFFF" : "#1A2A36"};
        ${mq["sm"]} {
          margin-top: 32px;
        }
      `}
    >
      <Container>
        <div
          css={css`
            ${mq["md"]} {
              margin-top: 0px;
            }
          `}
        >
          <Heading
            level={1}
            css={css`
              max-width: 1200px;
              margin: auto;
              font-weight: 400;
              font-size: 28px;
              line-height: 130%;
              color: ${heroBackgroundIsDark ? "#FFFFFF" : "#1A2A36"};
            `}
          >
            <Balancer
              dangerouslySetInnerHTML={{
                __html: (
                  fields?.heroHeading ||
                  "End-to-End Youth Sports Management Software for Volunteers"
                ).replace(/\. /, ".<br>"),
              }}
            ></Balancer>
          </Heading>
          <Body
            css={css`
              max-width: 925px;
              margin: 14px auto 0;
              font-weight: 400;
              font-size: 16px;
              line-height: 140%;
              color: ${heroBackgroundIsDark ? "#FFFFFF" : "#1A2A36"};
            `}
          >
            <Balancer>{fields.heroSubheading || ""}</Balancer>
          </Body>

          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              margin-top: 32px;
            `}
          >
            <GetStartedButton />
          </div>

          <div
            css={css`
              display: flex;
              gap: 12px;
              flex-wrap: wrap;
              justify-content: center;
              gap: 24px;
              margin-top: 24px;
              ${mq["sm"]} {
                gap: 0px;
                margin-top: 16px;
                flex-direction: column;
                align-items: center;
              }
              ${mq["md"]} {
                margin-top: 48px;
                flex-direction: row;
                align-items: center;
                justify-content: center;
              }
            `}
          >
            <div
              css={css`
                flex-direction: column;
              `}
            >
              <div
                css={css`
                  display: flex;
                  justify-content: center;
                  gap: 4px;
                  width: 100%;
                  display: flex;
                  flex-direction: column;
                  gap: 8px;
                  margin-top: 16px;
                  ${mq["sm"]} {
                    flex-direction: row;
                    gap: 16px;
                  }
                  ${mq["md"]} {
                    margin-top: unset;
                  }
                  align-items: center;
                `}
              >
                <div
                  css={css`
                    width: auto;
                    max-width: max-content;
                    div {
                      font-weight: 700;
                    }
                  `}
                >
                  <ReviewBadgeHero2
                    imageSrc="/images/icons/g2_icon_2.svg"
                    starCount={4.8}
                    reviewCount={process.env.g2Count || 150}
                    color={heroBackgroundIsDark ? "#FFFFFF" : "#1A2A36"}
                    background={
                      heroBackgroundIsDark
                        ? "rgba(0, 0, 0, 0.1)"
                        : "linear-gradient(223.53deg, rgba(56, 171, 255, 0.15) 33.5%, rgba(209, 224, 255, 0.15) 95.74%)"
                    }
                    borderColor={
                      heroBackgroundIsDark
                        ? "none"
                        : "rgba(255, 255, 255, 0.65)"
                    }
                  />
                </div>

                <div
                  css={css`
                    width: auto;
                    max-width: max-content;
                    div {
                      font-weight: 700;
                    }
                  `}
                >
                  <ReviewBadgeHero2
                    imageSrc="/images/icons/trustpilot_icon.svg"
                    starCount={5.0}
                    reviewCount={process.env.trustpilotCount || 1000}
                    color={heroBackgroundIsDark ? "#FFFFFF" : "#1A2A36"}
                    background={
                      heroBackgroundIsDark
                        ? "rgba(0, 0, 0, 0.1)"
                        : "linear-gradient(223.53deg, rgba(56, 171, 255, 0.15) 33.5%, rgba(209, 224, 255, 0.15) 95.74%)"
                    }
                    borderColor={
                      heroBackgroundIsDark
                        ? "none"
                        : "rgba(255, 255, 255, 0.65)"
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          css={css`
            position: relative;
            width: 100%;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-grow: 1;
              flex-direction: column;
              position: relative;
            `}
          >
            <div
              css={css`
                margin-top: 32px;
                margin-bottom: 24px;
                margin-right: calc(-1 * (100vw - 100%) / 2);
                ${mq["sm"]} {
                  margin-bottom: 40px;
                }
                ${mq["md"]} {
                  margin-bottom: 64px;
                }
              `}
            >
              <HomepageHeroTestimonials
                testimonials={
                  fields.testimonialsHero?.map((t: any) => t.fields) || []
                }
              />
            </div>
          </div>
        </div>
      </Container>

      {!isXs && (
        <div
          css={css`
            display: flex;
            flex: 1;
            padding-left: 16px;
            padding-right: 16px;
            ${mq["sm"]} {
              padding-left: 40px;
              padding-right: 40px;
            }
            @media (max-width: ${mq["md"]}px) {
              flex: 1;
              display: flex;
              justify-content: center;
              align-items: center;
              margin-top: 32px;
            }
          `}
        >
          <div
            css={css`
              position: relative;
              width: 100%;
              padding: 0;
              ${mq["sm"]} {
                display: flex;
              }
              @media (max-width: ${mq["md"]}px) {
                position: relative;
                width: calc(100% + 20px);
                margin-left: -20px;
                margin-right: -20px;
                height: 100%;
              }
            `}
          >
            {(isSm || isMd) &&
              isClient &&
              router.asPath.includes("youth-sports-management-software") && (
                <Image
                  src="/images/icons/users.svg"
                  alt="Users Icon over Hero Image"
                  width={163}
                  height={163}
                  css={css`
                    position: absolute;
                    ${mq["md"]} {
                      top: -40px;
                      left: 70%;
                      transform: translateX(-50%);
                      width: 160px;
                      height: 160px;
                    }
                    ${mq["sm"]} {
                      top: -40px;
                      left: 70%;
                      transform: translateX(-50%);
                      width: 124px;
                      height: 124px;
                    }
                  `}
                />
              )}
            {isSm && (
              <Image
                src={fields.heroImageSm.fields.file.url}
                loader={contentfulLoader}
                priority
                width={1188}
                height={760}
                alt="Homepage hero image"
                css={css`
                  display: none;
                  ${mq["sm"]} {
                    display: block;
                    width: 100%;
                    height: auto;
                  }
                  ${mq["md"]} {
                    display: none;
                  }
                `}
              />
            )}
            {isMd && (
              <Image
                src={fields.heroImageMd.fields.file.url}
                loader={contentfulLoader}
                priority
                width={1300}
                height={831}
                alt="Homepage hero image"
                css={css`
                  border-radius: 16px 0 0;
                  width: 100%;
                  ${mq["md"]} {
                    width: 100%;
                    height: auto;
                  }
                `}
              />
            )}
          </div>
        </div>
      )}

      {isXs && fields.heroImageXs && (
        <div
          css={css`
            position: relative;
            width: 100%;
            padding: 0;
            padding-left: 16px;
            margin-top: 42px;
          `}
        >
          {isClient &&
            router.asPath.includes("youth-sports-management-software") && (
              <Image
                src="/images/icons/users.svg"
                alt="Users Icon over Hero Image"
                width={112}
                height={112}
                css={css`
                  position: absolute;
                  top: -40px;
                  left: 80%;
                  transform: translateX(-50%);
                `}
              />
            )}
          <Image
            src={fields.heroImageXs.fields.file.url}
            loader={contentfulLoader}
            priority
            width={623}
            height={290}
            alt="Homepage hero image"
            css={css`
              display: flex;
              justify-content: end;
              width: 100%;
              height: auto;
            `}
          />
        </div>
      )}
    </div>
  );
};

export default SmallScreenHero;
